import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, providerImage } from "@/lib/api";
import { useCompare } from "@/context/CompareContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MapPin, ShieldCheck, Star, Check, ArrowRight, X, Crown, ExternalLink, GitCompare } from "lucide-react";
import { Disclaimer, B9Disclaimer } from "@/components/Disclaimers";

const money = (n) => `$${Number(n).toLocaleString()}`;

const CATEGORY_GRADIENT = {
  "Surgical": "linear-gradient(135deg, #0D5C75, #2A9D8F)",
  "Clinical & Diagnostics": "linear-gradient(135deg, #1E40AF, #3B82F6)",
  "Rehabilitation & Recovery": "linear-gradient(135deg, #065F46, #10B981)",
  "Wellness & Alternative": "linear-gradient(135deg, #92400E, #D97706)",
  "Specialized Care": "linear-gradient(135deg, #5B21B6, #8B5CF6)",
  "Women & Children": "linear-gradient(135deg, #9D174D, #EC4899)",
};

function SuccessRing({ value }) {
  return (
    <div className="relative h-16 w-16 shrink-0" title={`${value}% success rate`}>
      <svg viewBox="0 0 36 36" className="h-16 w-16 -rotate-90">
        <circle cx="18" cy="18" r="16" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="3" />
        <circle cx="18" cy="18" r="16" fill="none" stroke="#FBBF24"
          strokeWidth="3" strokeLinecap="round" strokeDasharray={`${(value / 100) * 100.5} 100.5`} />
      </svg>
      <span className="absolute inset-0 grid place-items-center text-xs font-bold text-white">{value}%</span>
    </div>
  );
}

export default function ProviderProfileModal({ provider, open, onOpenChange }) {
  const navigate = useNavigate();
  const compare = useCompare();
  const [activeIdx, setActiveIdx] = useState(0);
  const [failed, setFailed] = useState({});

  useEffect(() => { setActiveIdx(0); setFailed({}); }, [provider]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onOpenChange(false);
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [open, onOpenChange]);

  if (!open || !provider) return null;

  const facilities = provider.facilities?.length
    ? provider.facilities
    : ["International Patient Lounge", "24/7 Interpreter Services", "Airport Pickup", "Visa Assistance Letter", "Post-Op Follow-Up Teleconsultation"];

  const isB9 = provider.is_b9 || (provider.specialties || []).some((s) => s.startsWith("B9Concept"));

  const book = (pkg) => {
    const specialty = pkg?.specialty || provider.specialties?.[0] || "";
    const q = new URLSearchParams({ provider: provider.id, specialty });
    if (pkg) q.set("pkg", pkg.id);
    navigate(`/book?${q.toString()}`);
  };

  const compareSimilar = async () => {
    let picks = [];
    try {
      const { data } = await api.get("/v1/providers/map");
      const others = (data.providers || []).filter((x) => x.id !== provider.id);
      const sameCity = others.filter((x) => x.city === provider.city);
      const sameSpec = others.filter((x) => (x.specialties || []).some((s) => (provider.specialties || []).includes(s)));
      for (const list of [sameCity, sameSpec, others]) {
        for (const o of list) { if (picks.length >= 2) break; if (!picks.includes(o.id)) picks.push(o.id); }
        if (picks.length >= 2) break;
      }
    } catch { /* fall through with whatever picks we have */ }
    const ids = [provider.id, ...picks];
    compare?.setMany(ids);
    onOpenChange(false);
    navigate(`/compare?ids=${ids.join(",")}`);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={() => onOpenChange(false)} data-testid="provider-profile-modal">
      <div className="tricolor-top bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}>
        {/* Gradient header */}
        <div className="mv-gradient-hero px-6 pt-6 pb-5 text-white relative">
          <button onClick={() => onOpenChange(false)}
            className="absolute top-4 right-4 grid place-items-center h-8 w-8 rounded-full bg-white/15 hover:bg-white/25 transition-colors duration-200"
            data-testid="close-profile-btn"><X size={18} /></button>
          <div className="flex items-start gap-4 pr-10">
            <SuccessRing value={provider.success_rate || 0} />
            <div className="flex-1 min-w-0">
              <h2 className="font-heading text-2xl font-extrabold leading-tight">{provider.name}</h2>
              <p className="text-sm text-white/85 flex items-center gap-1 mt-0.5"><MapPin size={14} /> {provider.city}</p>
              <div className="flex flex-wrap items-center gap-2 mt-3">
                {(provider.accreditation || []).map((a) => (
                  <Badge key={a} className="bg-white/15 border border-white/30 text-white"><ShieldCheck size={12} className="mr-1" />{a}</Badge>
                ))}
                <span className="flex items-center gap-1 text-sm text-white/90"><Star size={14} fill="#FBBF24" className="text-[#FBBF24]" /> {provider.rating}</span>
                {provider.tier === "premium" && <Badge className="bg-[#FBBF24] text-[#101828] border-0"><Crown size={12} className="mr-1" />Premium Provider</Badge>}
                {provider.tier === "enterprise" && <Badge className="bg-[#8B5CF6] text-white border-0"><Crown size={12} className="mr-1" />Enterprise Partner</Badge>}
                {provider.featured && <Badge className="bg-[#F97316] text-white border-0">Featured</Badge>}
              </div>
            </div>
          </div>
        </div>

        {(() => {
          const images = provider.images || [];
          const gradient = CATEGORY_GRADIENT[provider.category] || CATEGORY_GRADIENT["Surgical"];
          const heroSrc = images[activeIdx] ? providerImage(provider.id, activeIdx) : null;
          const heroOk = heroSrc && !failed[activeIdx];
          return (
            <div className="bg-[#F7F9FC]">
              <div className="relative h-[280px] w-full" style={{ background: gradient }}>
                {heroOk ? (
                  <img src={heroSrc} alt={`${provider.name} facility`} loading="lazy" decoding="async"
                    onError={() => setFailed((f) => ({ ...f, [activeIdx]: true }))}
                    className="absolute inset-0 h-full w-full object-cover bg-[#F3F4F6]" data-testid="profile-hero-image" />
                ) : (
                  <div className="absolute inset-0 grid place-items-center text-center px-6" data-testid="profile-hero-placeholder">
                    <div>
                      <div className="font-heading font-extrabold text-white text-2xl leading-tight drop-shadow">{provider.name}</div>
                      <div className="text-white/85 text-sm mt-1">{provider.city}, India</div>
                    </div>
                  </div>
                )}
              </div>
              {images.length > 1 && (
                <div className="flex gap-2 px-6 py-3 overflow-x-auto no-scrollbar" data-testid="profile-gallery">
                  {images.map((_, i) => (
                    <button key={i} onClick={() => setActiveIdx(i)}
                      className={`h-16 w-24 shrink-0 rounded-lg overflow-hidden border-2 transition-colors duration-200 ${i === activeIdx ? "border-[#0D5C75]" : "border-transparent"}`}
                      data-testid={`gallery-thumb-${i}`}>
                      <img src={providerImage(provider.id, i)} alt={`${provider.name} view ${i + 1}`} loading="lazy" decoding="async"
                        onError={() => setFailed((f) => ({ ...f, [i]: true }))}
                        className="h-full w-full object-cover bg-[#E4E7EC]" style={{ background: failed[i] ? gradient : undefined }} />
                    </button>
                  ))}
                </div>
              )}
              {provider.website_url && (
                <div className="px-6 pb-3 pt-1">
                  <a href={provider.website_url} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-[#0D5C75] hover:underline"
                    data-testid="visit-website-link">
                    Visit Official Website <ExternalLink size={14} />
                  </a>
                </div>
              )}
            </div>
          );
        })()}

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          <section>
            <h3 className="font-heading font-bold text-[#101828] mb-2">About</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">{provider.about}</p>
            <div className="flex flex-wrap gap-2 mt-3">
              {(provider.specialties || []).map((s) => (
                <Badge key={s} variant="outline" className="border-[#0D5C75]/30 text-[#0D5C75]">{s}</Badge>
              ))}
            </div>
          </section>

          <section>
            <h3 className="font-heading font-bold text-[#101828] mb-3">Treatment packages</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {(provider.packages || []).map((pkg) => (
                <div key={pkg.id} className="rounded-xl border border-[#E4E7EC] p-4 hover:shadow-md transition-shadow duration-200" data-testid={`profile-package-${pkg.id}`}>
                  <div className="font-medium text-sm text-[#101828]">{pkg.name}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{pkg.specialty}{pkg.duration_days ? ` · ${pkg.duration_days} days` : ""}</div>
                  <div className="font-heading font-extrabold text-[#0D5C75] text-lg mt-2">{money(pkg.price)}</div>
                  <Button size="sm" className="mt-3 w-full rounded-full bg-[#2A9D8F] text-white hover:bg-[#227d72] hover:text-white"
                    onClick={() => book(pkg)} data-testid={`select-package-${pkg.id}`}>Select Package</Button>
                </div>
              ))}
            </div>
          </section>

          <section>
            <h3 className="font-heading font-bold text-[#101828] mb-3">Facilities & amenities</h3>
            <div className="grid sm:grid-cols-2 gap-2">
              {facilities.map((f) => (
                <div key={f} className="flex items-center gap-2 text-sm text-[#101828]">
                  <span className="grid place-items-center h-5 w-5 rounded-full bg-[#D1FAE5] text-[#087443] shrink-0"><Check size={12} /></span>{f}
                </div>
              ))}
            </div>
          </section>

          <section>
            <h3 className="font-heading font-bold text-[#101828] mb-3">Location</h3>
            <div className="rounded-xl border border-dashed border-[#0D5C75]/30 bg-[#E9F5F9] p-6 flex flex-col items-center text-center" data-testid="location-placeholder">
              <MapPin size={28} className="text-[#0D5C75]" />
              <div className="font-medium text-[#101828] mt-2">{provider.city}, India</div>
              <div className="text-xs text-muted-foreground">Map integration coming soon</div>
            </div>
          </section>

          <Disclaimer testid="verification-disclaimer">
            SarvaSeva verifies provider accreditation status (NABH/JCI) at onboarding and revalidates every 180 days. We do not independently verify individual clinical outcomes or practitioner qualifications. Patients are encouraged to conduct their own research and seek second opinions.
          </Disclaimer>

          {isB9 && <B9Disclaimer testid="b9-provider-disclaimer" />}
        </div>

        <div className="px-6 py-4 border-t border-[#E4E7EC] bg-[#F7F9FC] flex flex-col sm:flex-row gap-2.5">
          <Button variant="outline" className="sm:w-auto h-11 rounded-full border-[#0D5C75]/30 text-[#0D5C75] hover:bg-[#E9F5F9]"
            onClick={compareSimilar} data-testid="compare-similar-btn">
            <GitCompare size={16} className="mr-1.5" /> Compare with Similar
          </Button>
          <Button className="flex-1 h-11 rounded-full bg-[#D97706] text-white hover:bg-[#b45f05] hover:text-white"
            onClick={() => book(null)} data-testid="book-with-provider-btn">
            Book with this Provider <ArrowRight size={16} className="ml-1.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}
